/**
 * aegis7PresentationContract — AEGIS-7 presentation contract validator.
 *
 * Authoritative validator for the three-component presentation contract
 * defined in docs/doctrine/AEGIS-PRESENTATION-CONTRACT-DOCTRINE-1.md:
 *
 *   1. Calibrated confidence — top-level `confidence`, number in [0, 1], or
 *      null only when finalDisposition is 'failed' or 'enforcer_blocked'.
 *   2. Reasoning trace — top-level `reasoningTrace`. For high-stakes
 *      (materialityBand MATERIAL|EXISTENTIAL) calls, an inline non-empty
 *      array of { step, evidence } records. For ROUTINE calls, may instead
 *      be a { decisionIdRef } pointer to aegis_reasoning_chains.
 *   3. Dignity gate verdict — top-level `dignityVerdict`,
 *      { passed: boolean, violations: string[] }.
 *
 * Called by governedAegisCall._applyDisciplineEnforcer pre-return.
 *
 * Returns { passed, violations[] }. NEVER throws; callers attach the verdict
 * to the response and set finalDisposition='contract_violation' on failure.
 */

// KAUDIT-8 v1.1 — infantilizing / autonomous-execution UX phrases.
// Matched case-insensitively against the response `text` field.
// Keep narrow: only phrases with no legitimate non-UX usage in AEGIS output.
const INFANTILIZING_PATTERNS = Object.freeze([
  'don\'t worry',
  'do not worry',
  'no need to worry',
  'let me handle this for you',
  'i\'ll take care of this for you',
  'leave it to me',
  'sit back and relax',
  'i\'ve got this',
  'trust me',
  'just trust',
  'no questions asked',
  'i\'ll just go ahead and',
  'i went ahead and',
  'i\'ve already done',
]);

// High-stakes materiality bands (per COG-META-1 / KCRIT taxonomy).
const HIGH_STAKES_BANDS = new Set(['MATERIAL', 'EXISTENTIAL']);

// Dispositions for which `confidence: null` is acceptable.
const CONFIDENCE_NULLABLE_DISPOSITIONS = new Set([
  'failed',
  'enforcer_blocked',
  'kcrit_blocked',
]);

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function validateConfidence(response, violations) {
  const c = response.confidence;
  const disposition = response.finalDisposition;
  if (c === null || c === undefined) {
    if (!CONFIDENCE_NULLABLE_DISPOSITIONS.has(disposition)) {
      violations.push(
        `confidence is required (number in [0, 1]) when finalDisposition='${disposition || 'completed'}'`
      );
    }
    return;
  }
  if (typeof c !== 'number' || Number.isNaN(c)) {
    violations.push(`confidence must be a number; got ${typeof c}`);
    return;
  }
  if (c < 0 || c > 1) {
    violations.push(`confidence must be in [0, 1]; got ${c}`);
  }
}

function validateReasoningTrace(response, params, violations) {
  const trace = response.reasoningTrace;
  const materialityBand = params?.context?.materialityBand || 'ROUTINE';
  const isHighStakes = HIGH_STAKES_BANDS.has(materialityBand);

  if (trace === null || trace === undefined) {
    violations.push('reasoningTrace is required on every AEGIS response');
    return;
  }

  if (Array.isArray(trace)) {
    if (isHighStakes && trace.length === 0) {
      violations.push(
        `reasoningTrace must be a non-empty array on high-stakes (materialityBand=${materialityBand}) calls`
      );
      return;
    }
    for (let i = 0; i < trace.length; i++) {
      const item = trace[i];
      if (!isPlainObject(item)) {
        violations.push(`reasoningTrace[${i}] must be an object with shape { step, evidence }`);
        continue;
      }
      if (typeof item.step !== 'string' || item.step.trim() === '') {
        violations.push(`reasoningTrace[${i}].step must be a non-empty string`);
      }
      if (!('evidence' in item)) {
        violations.push(`reasoningTrace[${i}] must have an 'evidence' key (string or null)`);
      } else if (item.evidence !== null && typeof item.evidence !== 'string') {
        violations.push(`reasoningTrace[${i}].evidence must be a string or null`);
      }
    }
    return;
  }

  if (isPlainObject(trace) && typeof trace.decisionIdRef === 'string' && trace.decisionIdRef.trim() !== '') {
    if (isHighStakes) {
      violations.push(
        `reasoningTrace must be an inline non-empty array on high-stakes (materialityBand=${materialityBand}) calls; decisionIdRef pointer is only valid for ROUTINE calls`
      );
    }
    return;
  }

  violations.push(
    'reasoningTrace must be either a non-empty array of { step, evidence } or an object { decisionIdRef: string }'
  );
}

function validateDignityVerdict(response, violations) {
  const dv = response.dignityVerdict;
  if (!isPlainObject(dv)) {
    violations.push('dignityVerdict is required; must be an object { passed: boolean, violations: string[] }');
    return;
  }
  if (typeof dv.passed !== 'boolean') {
    violations.push('dignityVerdict.passed must be a boolean');
  }
  if (!Array.isArray(dv.violations)) {
    violations.push('dignityVerdict.violations must be an array of strings');
  }
}

/**
 * Apply the KAUDIT-8 dignity gate to a response text. Returns the verdict.
 * Exposed so the wrapper can compute and attach the verdict when callers
 * haven't pre-populated it.
 *
 * @param {string|null|undefined} text
 * @returns {{ passed: boolean, violations: string[] }}
 */
export function applyDignityGate(text) {
  if (text === null || text === undefined || text === '') {
    return { passed: true, violations: [] };
  }
  if (typeof text !== 'string') {
    return { passed: false, violations: ['dignity_gate: response text must be a string'] };
  }
  const lowered = text.toLowerCase();
  const hits = [];
  for (const phrase of INFANTILIZING_PATTERNS) {
    if (lowered.includes(phrase)) hits.push(phrase);
  }
  return { passed: hits.length === 0, violations: hits };
}

/**
 * Validate that a response satisfies the presentation contract.
 *
 * @param {object} response — the candidate AEGIS response object
 * @param {object} params   — the original call params (used for materialityBand)
 * @returns {{ passed: boolean, violations: string[] }}
 */
export function validatePresentationContract(response, params) {
  const violations = [];
  if (!isPlainObject(response)) {
    return { passed: false, violations: ['response must be an object'] };
  }
  validateConfidence(response, violations);
  validateReasoningTrace(response, params, violations);
  validateDignityVerdict(response, violations);
  return { passed: violations.length === 0, violations };
}

export const PRESENTATION_CONTRACT_FIELDS = Object.freeze([
  'confidence',
  'reasoningTrace',
  'dignityVerdict',
]);

export const INFANTILIZING_PATTERN_LIST = INFANTILIZING_PATTERNS;

export default validatePresentationContract;
